import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaCreditCard, FaLock, FaShoppingCart, FaTimes } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { checkoutPhotoCart } from '../services/api';
import './Checkout.css';

const getPhotoDisplayName = (photo) => {
  const name = photo?.name || photo?.title || photo?.filename || `Photo ${photo?.id || ''}`;
  return String(name).split('/').pop() || name;
};

function Checkout() {
  const { user } = useAuth();
  const { items, total, removeItem, clearCart } = useCart();
  const navigate = useNavigate();
  const [checkingOut, setCheckingOut] = useState(false);
  const [error, setError] = useState('');

  const handlePay = async () => {
    if (items.length === 0) {
      setError('Votre panier est vide.');
      return;
    }

    setCheckingOut(true);
    setError('');

    try {
      const response = await checkoutPhotoCart(items.map(item => item.id), {
        billing_email: user?.email || '',
      });

      if (response?.already_completed) {
        clearCart();
        navigate('/payment-history');
        return;
      }

      if (!response?.payment_url) {
        throw new Error("CinetPay n'a pas renvoye de lien de paiement.");
      }

      window.location.assign(response.payment_url);
    } catch (err) {
      setError(err.message || 'Impossible de demarrer le paiement.');
      setCheckingOut(false);
    }
  };

  return (
    <div className="checkout-page">
      <section className="checkout-header">
        <Link to="/events" className="checkout-back-link">
          <FaArrowLeft />
          <span>Continuer mes achats</span>
        </Link>
        <div>
          <p className="checkout-kicker">Paiement PhotoMatch</p>
          <h1>Votre panier</h1>
          <p>CinetPay affichera les moyens de paiement disponibles sur sa page securisee.</p>
        </div>
      </section>

      <div className="checkout-grid">
        <section className="checkout-items-panel">
          <div className="checkout-panel-title">
            <FaShoppingCart />
            <h2>Photos selectionnees</h2>
          </div>

          {items.length === 0 ? (
            <div className="checkout-empty">
              <p>Aucune photo dans le panier.</p>
              <Link to="/events">Parcourir les evenements</Link>
            </div>
          ) : (
            <div className="checkout-items">
              {items.map(item => (
                <div key={item.id} className="checkout-item">
                  <div>
                    <strong>{getPhotoDisplayName(item)}</strong>
                    <span>Photo #{item.id}</span>
                  </div>
                  <div className="checkout-item-side">
                    <strong>{Number(item.price || 0).toFixed(2)} F</strong>
                    <button
                      type="button"
                      onClick={() => removeItem(item.id)}
                      aria-label={`Retirer ${getPhotoDisplayName(item)}`}
                    >
                      <FaTimes />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <aside className="checkout-summary">
          <h2>Resume</h2>
          <div className="checkout-summary-row">
            <span>Photos</span>
            <strong>{items.length}</strong>
          </div>
          <div className="checkout-summary-row">
            <span>Total</span>
            <strong>{total.toFixed(2)} F</strong>
          </div>
          {error && <div className="error-message checkout-error">{error}</div>}
          <button
            type="button"
            className="checkout-pay-button"
            disabled={checkingOut || items.length === 0}
            onClick={handlePay}
          >
            <FaCreditCard />
            <span>{checkingOut ? 'Ouverture de CinetPay...' : 'Payer avec CinetPay'}</span>
          </button>
          <p className="checkout-secure-note">
            <FaLock />
            <span>Le choix du moyen de paiement se fait directement sur CinetPay.</span>
          </p>
        </aside>
      </div>
    </div>
  );
}

export default Checkout;
